import React, { useState, useEffect } from 'react';
import { Star, ExternalLink } from 'lucide-react';
import { testimonials } from '../data/mock';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GoogleLogo = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3C33.7 32.7 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.2 6.1 29.4 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.6-.4-3.9z"/>
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.2 6.1 29.4 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.3 0-9.7-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
    <path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.6l6.2 5.2C41 35.4 44 30.2 44 24c0-1.3-.1-2.6-.4-3.9z"/>
  </svg>
);

const StarRow = ({ rating, size = 20 }) => (
  <div className="flex items-center gap-1">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star
        key={i}
        size={size}
        className={i <= Math.round(rating) ? 'text-primary fill-primary' : 'text-secondary'}
      />
    ))}
  </div>
);

export const GoogleReviewsSection = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/reviews/google`)
      .then((res) => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;

  const live = data && data.configured && data.reviews && data.reviews.length > 0;
  const reviews = live
    ? data.reviews
    : testimonials.slice(0, 6).map((t) => ({
        author: t.name,
        rating: t.rating,
        text: t.text,
        relativeTime: t.date,
      }));

  return (
    <section className="py-24 bg-page relative" data-testid="google-reviews-section">
      <div className="absolute inset-0 cyber-grid opacity-10"></div>
      <div className="container mx-auto px-6 relative z-10">
        <div className="text-center mb-12">
          <h2 className="heading-2 text-5xl md:text-6xl font-black uppercase mb-4">
            <span className="text-blue" style={{ textShadow: '0 0 15px var(--neon-blue)' }}>What Our </span>
            <span className="text-pink" style={{ textShadow: '0 0 15px var(--neon-pink)' }}>Guests Say</span>
          </h2>

          {live && (
            <div
              data-testid="google-rating-badge"
              className="inline-flex items-center gap-3 px-6 py-3 bg-card border-2 border-green rounded-full mt-2"
              style={{ boxShadow: '0 0 20px rgba(0, 255, 65, 0.3)' }}
            >
              <GoogleLogo size={26} />
              <span className="text-white text-2xl font-black">{data.rating}</span>
              <StarRow rating={data.rating} size={18} />
              <span className="text-secondary text-sm font-semibold">
                {data.totalReviews} Google reviews
              </span>
            </div>
          )}

          <p className="body-medium text-secondary max-w-2xl mx-auto mt-4">
            Real experiences from real people who've br8ken reality with us.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {reviews.slice(0, 6).map((review, index) => {
            const borderColors = ['border-green', 'border-blue', 'border-pink'];
            const glowColors = ['rgba(0, 255, 65, 0.3)', 'rgba(0, 217, 255, 0.3)', 'rgba(255, 0, 255, 0.3)'];
            return (
              <div
                key={index}
                data-testid={`google-review-card-${index}`}
                className={`bg-card border-2 ${borderColors[index % 3]} rounded-2xl p-6 hover:scale-105 transition-all duration-300 flex flex-col`}
                style={{ boxShadow: `0 0 15px ${glowColors[index % 3]}` }}
              >
                <div className="flex items-center justify-between mb-4">
                  <StarRow rating={review.rating} />
                  {live && <GoogleLogo size={18} />}
                </div>

                <p className="text-white text-sm leading-relaxed mb-4 flex-1">
                  &quot;{review.text}&quot;
                </p>

                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    {review.photo && (
                      <img src={review.photo} alt={review.author} className="w-8 h-8 rounded-full flex-shrink-0" referrerPolicy="no-referrer" />
                    )}
                    <p className="text-primary font-semibold truncate">{review.author}</p>
                  </div>
                  <p className="text-secondary text-xs whitespace-nowrap">{review.relativeTime}</p>
                </div>
              </div>
            );
          })}
        </div>

        {live && (
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mt-10">
            {data.writeReviewUrl && (
              <a
                href={data.writeReviewUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="leave-google-review-btn"
                className="inline-flex items-center gap-3 px-8 py-4 bg-card border-2 border-pink rounded-full text-pink font-black uppercase tracking-wider hover:scale-105 transition-transform duration-300"
                style={{ boxShadow: '0 0 20px rgba(255, 0, 255, 0.35)' }}
              >
                <GoogleLogo size={22} />
                Leave Us a Google Review
                <Star size={18} className="text-pink fill-pink" />
              </a>
            )}
            {data.mapsUrl && (
              <a
                href={data.mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="see-all-google-reviews-link"
                className="inline-flex items-center gap-2 text-blue font-bold uppercase tracking-wider hover:text-primary transition-colors"
              >
                See all {data.totalReviews} reviews on Google
                <ExternalLink size={16} />
              </a>
            )}
          </div>
        )}
      </div>
    </section>
  );
};
